// apps/web/src/pages/onboarding/[token]/hours.tsx — Phase 7 plan 07-06.
//
// Onboarding step 3: opening hours. The page renders 7 weekday rows
// (Mon..Sun); each row has 2 time inputs (open + close). An empty
// `open` or `close` is treated as a closed day (the row is omitted
// from the GraphQL input). On submit it calls
// `Mutation.saveOnboardingHours`; on success it navigates to the
// review step.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { WizardShell } from "../../../components/WizardShell";
import { gqlRequest } from "../../../lib/api";

type WeekdayKey = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

const WEEKDAYS: { key: WeekdayKey; i18nKey: string }[] = [
  { key: "MO", i18nKey: "availability.admin.slot.weekdayMo" },
  { key: "TU", i18nKey: "availability.admin.slot.weekdayTu" },
  { key: "WE", i18nKey: "availability.admin.slot.weekdayWe" },
  { key: "TH", i18nKey: "availability.admin.slot.weekdayTh" },
  { key: "FR", i18nKey: "availability.admin.slot.weekdayFr" },
  { key: "SA", i18nKey: "availability.admin.slot.weekdaySa" },
  { key: "SU", i18nKey: "availability.admin.slot.weekdaySo" },
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const SAVE_HOURS_MUTATION = /* GraphQL */ `
  mutation SaveOnboardingHours($input: OnboardingHoursInput!) {
    saveOnboardingHours(input: $input) {
      onboardingProgress
      restaurant {
        id
        slug
      }
    }
  }
`;

interface DayHours {
  open: string;
  close: string;
}

type HoursForm = Record<WeekdayKey, DayHours>;

const EMPTY_HOURS: HoursForm = {
  MO: { open: "", close: "" },
  TU: { open: "", close: "" },
  WE: { open: "", close: "" },
  TH: { open: "", close: "" },
  FR: { open: "", close: "" },
  SA: { open: "", close: "" },
  SU: { open: "", close: "" },
};

export function OnboardingHoursPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hours, setHours] = useState<HoursForm>(EMPTY_HOURS);
  const [errors, setErrors] = useState<Partial<Record<WeekdayKey, boolean>>>(
    {},
  );

  const mutation = useMutation({
    mutationFn: () => {
      const rows = WEEKDAYS.filter(
        ({ key }) => hours[key].open !== "" && hours[key].close !== "",
      ).map(({ key }) => ({
        weekday: key,
        openTime: hours[key].open,
        closeTime: hours[key].close,
        closeNextDay: false,
      }));
      return gqlRequest(SAVE_HOURS_MUTATION, { input: { rows } });
    },
    onSuccess: () => {
      navigate(`/onboarding/${token}/review`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors: Partial<Record<WeekdayKey, boolean>> = {};
    for (const { key } of WEEKDAYS) {
      const { open, close } = hours[key];
      const openEmpty = open === "";
      const closeEmpty = close === "";
      if (openEmpty !== closeEmpty) {
        nextErrors[key] = true;
        continue;
      }
      if (!openEmpty && (!TIME_RE.test(open) || !TIME_RE.test(close))) {
        nextErrors[key] = true;
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    mutation.mutate();
  }

  return (
    <WizardShell currentStep="hours">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <h1 className="font-heading text-2xl">{t("onboarding.hoursTitle")}</h1>
        <p className="text-sm text-text/70">{t("onboarding.hoursHelp")}</p>
        <ul className="flex flex-col gap-2">
          {WEEKDAYS.map(({ key, i18nKey }) => (
            <li
              key={key}
              className="flex items-center gap-2 border border-divider rounded-md px-3 py-2"
              data-testid={`hours-row-${key}`}
            >
              <span className="font-heading text-sm w-8">{t(i18nKey)}</span>
              <input
                type="time"
                value={hours[key].open}
                onChange={(e) =>
                  setHours((s) => ({
                    ...s,
                    [key]: { ...s[key], open: e.target.value },
                  }))
                }
                className="field"
                data-testid={`hours-open-${key}`}
                style={{ maxWidth: 110 }}
              />
              <span className="text-text/60">–</span>
              <input
                type="time"
                value={hours[key].close}
                onChange={(e) =>
                  setHours((s) => ({
                    ...s,
                    [key]: { ...s[key], close: e.target.value },
                  }))
                }
                className="field"
                data-testid={`hours-close-${key}`}
                style={{ maxWidth: 110 }}
              />
              {errors[key] ? (
                <span className="text-[11px] text-red-700" role="alert">
                  {t("onboarding.hoursInvalid")}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn btn-primary"
          data-testid="onboarding-hours-submit"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" aria-hidden />
              {t("onboarding.submitting")}
            </span>
          ) : (
            t("onboarding.saveNext")
          )}
        </button>
      </form>
    </WizardShell>
  );
}
